// Helpers tipados del MÓDULO 5 (Segunda instancia + Ejecución de sanciones +
// Archivo definitivo). Centraliza las llamadas al backend para que las vistas no
// repitan rutas/DTOs. Base de la API y token JWT los resuelve `api` (lib/api.ts).
// Todo requiere Bearer. Los bloqueos procesales llegan como error 422 con
// { message, code } (p. ej. BLOQUEO_EJECUTAR_SIN_EJECUTORIA,
// BLOQUEO_ARCHIVO_REQUISITOS); `api()` propaga el `message` como Error.

import { api } from './api';

// ---- Tipos / enums locales ------------------------------------------------

export type DecisionSegunda =
  | 'CONFIRMA'
  | 'REVOCA'
  | 'MODIFICA'
  | 'DECLARA_NULIDAD';

export const DECISION_SEGUNDA: DecisionSegunda[] = [
  'CONFIRMA',
  'REVOCA',
  'MODIFICA',
  'DECLARA_NULIDAD',
];

export type RetornaA = 'INSTRUCCION' | 'JUZGAMIENTO';
export const RETORNA_A: RetornaA[] = ['INSTRUCCION', 'JUZGAMIENTO'];

// TipoSancion (constante local; no viene en /catalogos).
export type TipoSancion =
  | 'DESTITUCION_INHABILIDAD_GENERAL'
  | 'SUSPENSION'
  | 'MULTA'
  | 'AMONESTACION_ESCRITA'
  | 'INHABILIDAD_ESPECIAL'
  | 'OTRA';

export const TIPO_SANCION: TipoSancion[] = [
  'DESTITUCION_INHABILIDAD_GENERAL',
  'SUSPENSION',
  'MULTA',
  'AMONESTACION_ESCRITA',
  'INHABILIDAD_ESPECIAL',
  'OTRA',
];

export type TipoCumplimiento = 'TOTAL' | 'PARCIAL';
export const TIPO_CUMPLIMIENTO: TipoCumplimiento[] = ['TOTAL', 'PARCIAL'];

// Estados del expediente relevantes para el módulo 5 (coincidencia laxa).
export const ESTADOS_SEGUNDA_INSTANCIA = ['SEGUNDA_INSTANCIA', 'RECURSO_INTERPUESTO'];
export const ESTADOS_EJECUCION = [
  'FALLO_EJECUTORIADO',
  'EJECUCION_SANCION',
  'SANCION_CUMPLIDA',
  'ARCHIVO_DEFINITIVO_HISTORICO',
];

// ---- Roles / RBAC de la UI ------------------------------------------------

// Reparto de segunda instancia: solo Director de segunda instancia / administrador.
export const ROLES_DIRECTOR_SEGUNDA = ['DIRECTOR_SEGUNDA_INSTANCIA', 'ADMINISTRADOR'];
// Decisión / actuaciones de segunda instancia.
export const ROLES_SEGUNDA = [
  'SEGUNDA_INSTANCIA',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'ADMINISTRADOR',
];
// Ejecución de sanciones y archivo definitivo.
export const ROLES_EJECUCION_SANCION = [
  'SECRETARIA',
  'DIRECTOR_JUZGAMIENTO',
  'DIRECTOR_SEGUNDA_INSTANCIA',
  'ADMINISTRADOR',
];

export interface UsuarioSesion {
  id?: string;
  nombre?: string;
  rol?: string;
  [k: string]: unknown;
}

/** Lee el usuario en sesión desde localStorage ('usuario'). */
export function usuarioActual(): UsuarioSesion | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('usuario');
    return raw ? (JSON.parse(raw) as UsuarioSesion) : null;
  } catch {
    return null;
  }
}

export function puedeRepartir2a(rol?: string): boolean {
  return !!rol && ROLES_DIRECTOR_SEGUNDA.includes(rol);
}
export function puedeDecidir2a(rol?: string): boolean {
  return !!rol && ROLES_SEGUNDA.includes(rol);
}
export function puedeEjecutarSancion(rol?: string): boolean {
  return !!rol && ROLES_EJECUCION_SANCION.includes(rol);
}

// ---- Segunda instancia ----------------------------------------------------

export function recepcionSegundaInstancia(
  expedienteId: string,
  body: {
    fecha: string;
    autoridadRemitente?: string;
    radicadoPrimera?: string;
    constanciaKey?: string;
    autoConcedeKey?: string;
    recursoKey?: string;
  },
): Promise<unknown> {
  return api(`/expedientes/${expedienteId}/segunda-instancia/recepcion`, { method: 'POST', body });
}

export function repartoSegundaInstancia(
  segundaId: string,
  body: { funcionarioId: string; observaciones?: string },
): Promise<unknown> {
  return api(`/segunda-instancia/${segundaId}/reparto`, { method: 'POST', body });
}

export function decisionSegundaInstancia(
  segundaId: string,
  body: {
    decision: DecisionSegunda;
    fecha: string;
    aspectosModificados?: string;
    retornaA?: RetornaA;
    tipoNulidad?: string;
    decisionKey?: string;
  },
): Promise<unknown> {
  return api(`/segunda-instancia/${segundaId}/decision`, { method: 'POST', body });
}

export function notificarSegundaInstancia(
  segundaId: string,
  body: {
    notificacionPersonal: boolean;
    fecha: string;
    notificacionKey?: string;
    edictoKey?: string;
  },
): Promise<unknown> {
  return api(`/segunda-instancia/${segundaId}/notificar`, { method: 'POST', body });
}

export function ejecutoriaSegundaInstancia(
  segundaId: string,
  body: { fecha: string },
): Promise<unknown> {
  return api(`/segunda-instancia/${segundaId}/ejecutoria`, { method: 'POST', body });
}

// ---- Sanciones ------------------------------------------------------------

export function crearSancion(
  expedienteId: string,
  body: {
    tipoSancion: TipoSancion;
    autoridadEjecutora: string;
    comunicacionKey?: string;
    fechaComunicacion: string;
  },
): Promise<{ id: string }> {
  return api<{ id: string }>(`/expedientes/${expedienteId}/sanciones`, { method: 'POST', body });
}

export function ejecucionSancion(
  sancionId: string,
  body: {
    ejecutada: boolean;
    fechaEjecucion?: string;
    actoEjecucionKey?: string;
    numeroActo?: string;
    observaciones?: string;
  },
): Promise<unknown> {
  return api(`/sanciones/${sancionId}/ejecucion`, { method: 'PATCH', body });
}

export function registroSancion(
  sancionId: string,
  body: { registrada: boolean; soporteRegistroKey?: string; fechaRegistro?: string },
): Promise<unknown> {
  return api(`/sanciones/${sancionId}/registro`, { method: 'PATCH', body });
}

export function cumplimientoSancion(
  sancionId: string,
  body: {
    cumplida: boolean;
    fechaCumplimiento?: string;
    tipoCumplimiento?: TipoCumplimiento;
  },
): Promise<unknown> {
  return api(`/sanciones/${sancionId}/cumplimiento`, { method: 'PATCH', body });
}

// ---- Archivo definitivo ---------------------------------------------------

export function archivarDefinitivo(
  expedienteId: string,
  body: { fecha: string; actaKey?: string },
): Promise<unknown> {
  return api(`/expedientes/${expedienteId}/archivar-definitivo`, { method: 'POST', body });
}

// ---- Helpers de detalle ---------------------------------------------------

/**
 * Extrae el id del registro de segunda instancia desde el detalle del expediente.
 * El backend puede exponerlo con distintos nombres de clave (segunda_instancia /
 * segundaInstancia). Devuelve '' si no está disponible.
 */
export function idSegundaInstancia(exp: unknown): string {
  const raw = exp as Record<string, unknown>;
  for (const k of ['segunda_instancia', 'segundaInstancia', 'segunda']) {
    const v = raw[k];
    if (v && typeof v === 'object' && 'id' in (v as object)) return String((v as { id: unknown }).id);
    if (typeof v === 'string' && v) return v;
  }
  return '';
}

/** ¿El expediente ya tiene registro de segunda instancia (recepción hecha)? */
export function tieneSegundaInstancia(exp: unknown): boolean {
  const raw = exp as Record<string, unknown>;
  return !!(raw['segunda_instancia'] || raw['segundaInstancia'] || raw['segunda']);
}

// Sanción tal como llega en GET /expedientes/:id (snake_case tolerante).
export interface SancionResumen {
  id: string;
  tipo_sancion?: string | null;
  tipoSancion?: string | null;
  autoridad_ejecutora?: string | null;
  autoridadEjecutora?: string | null;
  fecha_comunicacion?: string | null;
  fechaComunicacion?: string | null;
  ejecutada?: boolean | null;
  fecha_ejecucion?: string | null;
  numero_acto?: string | null;
  registrada?: boolean | null;
  fecha_registro?: string | null;
  cumplida?: boolean | null;
  fecha_cumplimiento?: string | null;
  tipo_cumplimiento?: string | null;
  [k: string]: unknown;
}

/** Lista de sanciones del expediente (tolera distintos nombres de clave). */
export function sanciones(exp: unknown): SancionResumen[] {
  const raw = exp as Record<string, unknown>;
  for (const k of ['sanciones', 'sancion']) {
    const v = raw[k];
    if (Array.isArray(v)) return v as SancionResumen[];
    if (v && typeof v === 'object' && 'id' in (v as object)) return [v as SancionResumen];
  }
  return [];
}

/** Id de una sanción (respaldo tolerante). */
export function idSancion(s: unknown): string {
  const raw = s as Record<string, unknown>;
  return raw && raw['id'] != null ? String(raw['id']) : '';
}
